import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { api, fullName, formatEUR, formatDate } from '../lib/api';
import { useAuth } from '../lib/auth';
import { isGestion, isManagement } from '../lib/roles';
// Phase 23.18 — Antes se importaba isManagement para bloquear acceso a
// roles administrativos. Ahora todos los autenticados pueden entrar.
import { PageHeader, EmptyState } from '../components/Common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { PatientCombobox } from '../components/PatientCombobox';
import { Plus, Download, Receipt, Trash2, FileSpreadsheet, AlertTriangle, ArrowLeft } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { PAYMENT_METHODS, computeCobroDesglose, signedCobroAmount } from '../lib/cobroQuickHelpers';
import { desgloseGuardado, esIvaIncluido } from '../lib/iva';
import {
  amountOptionsForConcept, canonicalCobroConcept, isDevolucionConcept, useCobroPresets,
} from '../lib/cobroPresets';
import { ConceptoCobroField } from '../components/cobros/ConceptoCobroField';
import RevisionIvaPanel from '../components/cobros/RevisionIvaPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
/**
 * Descarga con la cookie de sesión (Phase 22: cp_session httpOnly, de ahí
 * `credentials: 'include'`).
 *
 * 2026-09-17 — `fetch` NO lanza con un 403: devuelve una respuesta con el
 * cuerpo del error. Sin mirar el estado, esto guardaba en el disco un
 * «cobros_2026-09.xlsx» que por dentro era `{"detail":"Permisos
 * insuficientes"}`. Un fichero que Excel no abre y que nadie entiende. Ahora
 * se comprueba antes: o baja el Excel de verdad, o se dice lo que ha pasado.
 * Devuelve true solo si el fichero ha llegado, para que quien llama no cante
 * un «descargado» que no ha ocurrido.
 */
async function downloadAuth(url, filename) {
  try {
    const r = await fetch(url, { credentials: 'include' });
    if (!r.ok) {
      toast.error(r.status === 403
        ? 'No tienes permiso para descargar este Excel (es de dirección).'
        : 'No se ha podido descargar el Excel.');
      return false;
    }
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
    return true;
  } catch {
    toast.error('No se ha podido descargar el Excel.');
    return false;
  }
}
export default function CobrosPage() {
  const { user } = useAuth();
  // Corregir un cobro mal tecleado es de quien cobra. El servidor lo abrió a
  // todo el equipo salvo los perfiles clínicos el 2026-08-31 («cuando hay un
  // error en un cobro hay que poder corregirlo, y la trazabilidad ya apunta
  // quién cambió qué»), pero esta pantalla siguió escondiendo el lápiz y la
  // papelera a quien no fuera dirección — y como hasta hoy el mostrador no
  // veía el listado, no se notaba. Ahora recepción ve la caja del día: si viera un
  // cobro suyo mal puesto y no pudiera tocarlo, tendría que llamar a dirección
  // para arreglar su propio error (2026-09-17).
  //
  // `isGestion` y no «todo el que no sea clínico»: el servidor deja también a
  // enfermería, pero nadie ha pedido eso y enfermería no cuadra la caja. Se
  // abre a quien lo necesita —dirección y mostrador— y no un palmo más.
  const canEditDelete = isGestion(user);
  // 2026-09-17 — El Excel de cobros lo sirve require_management(): a una
  // administrativa el servidor le contesta 403. Aquí se ESCONDE el botón (no
  // se avisa) porque no hay nada que la persona pueda hacer al respecto: un
  // botón que siempre falla solo estorba, y la página ya avisa por su cuenta
  // cuando el listado no se puede cargar. Lo mismo que ya se hace con el
  // botón de borrar (`canEditDelete`).
  const canExportExcel = isManagement(user);
  // 2026-09-17: las administrativas ven la caja del día, pero no los KPIs
  // ni el mes. De aquí cuelga TODA la pantalla: dirección
  // sigue con el mes, la búsqueda y el histórico; el mostrador ve la caja de
  // UN día para cuadrarla. No es un adorno de interfaz: el servidor rechaza
  // el mes y el histórico a quien no es dirección (`_caja_del_dia_o_nada`),
  // así que esto solo evita ofrecer una puerta que va a dar 403.
  const esDireccion = isManagement(user);
  const [searchParams] = useSearchParams();
  const initialPatient = searchParams.get('patient');
  const [items, setItems] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  // Por qué no se han podido cargar. Se queda en pantalla: un toast se va
  // en cuatro segundos y deja detrás un «0 cobros» que parece un dato.
  const [fallo, setFallo] = useState(null);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  // El día que se cuadra. Arranca en hoy, que es lo normal, pero se puede
  // mover: la caja se cierra por la tarde y se repasa a la mañana siguiente
  // (y el lunes se repasa la del viernes). Un día cualquiera, sí; un rango o
  // un mes, nunca —ni aquí ni en el servidor—.
  const [dia, setDia] = useState(() => new Date().toISOString().slice(0, 10));
  const [q, setQ] = useState('');
  const [form, setForm] = useState({
    patient_id: initialPatient || '', patient_name: '', nif: '', address: '', concept: '',
    vat_applied: false,
    date: new Date().toISOString().slice(0, 10), notes: ''
  });
  // Phase 24.C — líneas desglosadas. Mínimo 1 línea (modo simple compatible).
  const [lines, setLines] = useState([
    { amount: '', payment_method: '', concept: '' },
  ]);
  const [busy, setBusy] = useState(false);
  // Revisar el IVA de los cobros de antes del 24/9/2026 (dirección): la
  // pestaña y cuántos quedan por decidir, en el botón. '?' si no se ha podido
  // contar: el motivo se ve al abrirla.
  const [vistaIva, setVistaIva] = useState(false);
  const [ivaPendientes, setIvaPendientes] = useState(null);
  const total = useMemo(() => items.reduce((s, c) => s + Number(c.total || c.amount || 0), 0), [items]);
  const linesTotal = useMemo(() => lines.reduce((s, l) => s + Number(l.amount || 0), 0), [lines]);
  // Las líneas son lo que paga el paciente; con IVA, el 21% va DENTRO
  // (24/9/2026): 100 → base 82,64 + IVA 17,36 = 100.
  const desgloseLineas = useMemo(
    () => computeCobroDesglose(linesTotal, form.vat_applied, form.concept),
    [linesTotal, form.vat_applied, form.concept],
  );
  const cobroPresets = useCobroPresets();
  const amountOptions = amountOptionsForConcept(form.concept, cobroPresets);
  // Devolución a mano (2026-09-08): se escribe en positivo, se
  // guarda en negativo y sin IVA; en la caja del mes resta como las que
  // vuelca «Facturas y devoluciones».
  const esDevolucion = isDevolucionConcept(form.concept);

  // Phase 23.18 — Antes el guard redirigía a "/" si el rol no era
  // management. Eso rompía el flujo "Pacientes → ficha → + Registrar cobro"
  // para usuarios administrativos (enfermería). Ahora permitimos a cualquier rol
  // autenticado acceder a /cobros; el listado mensual ya se filtra a nivel
  // backend, y registrar un cobro es una acción operativa habitual.
  // (No hay redirect.)

  const fetchItems = async () => {
    setLoading(true);
    try {
      const params = {};
      if (esDireccion) {
        if (month) params.month = month;
        if (q) params.q = q;
      } else {
        // UN día, y nada más: ni mes, ni búsqueda, ni paciente. Es lo único
        // que el servidor le va a dar, y pedir de más sería un 403 seguro.
        params.date = dia;
      }
      const r = await api.get('/api/cobros', { params });
      setItems(Array.isArray(r.data) ? r.data : []);
      setFallo(null);
    } catch (err) {
      setItems([]);
      // Un 403 aquí NO es «no hay cobros»: es «no puedes verlos». Dejarlo en
      // «Total 0,00 € · 0 cobros» con un toast que se va en cuatro segundos
      // hacía creer que la caja del mes estaba vacía — en LA pantalla de
      // dinero (2026-09-17). El aviso se queda puesto hasta que se resuelva.
      setFallo(err?.response?.status === 403
        ? 'No tienes permiso para ver los cobros. Lo que se ve abajo no son todos: no es ninguno.'
        : (err?.response?.data?.detail || 'No se han podido cargar los cobros. Esto NO significa que no haya.'));
      toast.error(err?.response?.data?.detail || 'No se pudieron cargar los cobros');
    } finally { setLoading(false); }
  };
  // Sin catch, un fallo aquí dejaba el buscador de pacientes vacío sin decir
  // nada: se acababa escribiendo el nombre a mano y el cobro se quedaba sin
  // ficha, fuera del saldo de esa paciente.
  useEffect(() => {
    api.get('/api/patients')
      .then(r => setPatients(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error('No se ha podido cargar la lista de pacientes: busca por nombre y comprueba la ficha antes de guardar'));
  }, []);
  useEffect(() => { const t = setTimeout(fetchItems, 200); return () => clearTimeout(t); }, [month, q, dia, esDireccion]);
  useEffect(() => {
    if (!esDireccion) return;
    api.get('/api/cobros/revision-iva')
      .then((r) => {
        const n = r.data?.resumen?.pendientes;
        setIvaPendientes(typeof n === 'number' ? n : null);
      })
      .catch(() => setIvaPendientes('?'));
  }, [esDireccion]);
  const alternarVistaIva = () => {
    // Al volver, la lista se pide de nuevo: lo decidido cambia totales.
    if (vistaIva) fetchItems();
    setVistaIva((v) => !v);
  };

  // El buscador nos pasa la ficha elegida: puede venir de la búsqueda en
  // servidor y no estar entre los pacientes precargados aquí (son los 200 más
  // recientes). Antes, con un paciente antiguo el cobro salía
  // sin nombre, sin NIF y sin dirección.
  const onPatientPick = (id, elegido) => {
    const p = elegido || patients.find(x => x.id === id);
    setForm(f => ({
      ...f,
      patient_id: id,
      patient_name: fullName(p),
      nif: p?.nif || '',
      address: [p?.address?.calle, p?.address?.poblacion, p?.address?.provincia, p?.address?.codigo_postal].filter(Boolean).join(', '),
    }));
  };

  // Phase 24.C — manipulación de líneas
  const updateLine = (idx, key, value) => {
    setLines(ls => ls.map((ln, i) => i === idx ? { ...ln, [key]: value } : ln));
  };
  const addLine = () => {
    setLines(ls => [...ls, { amount: '', payment_method: '', concept: '' }]);
  };
  const removeLine = (idx) => {
    setLines(ls => ls.length > 1 ? ls.filter((_, i) => i !== idx) : ls);
  };
  const updateConcept = (value, suggested) => {
    setForm((current) => ({
      ...current,
      concept: value,
      vat_applied: isDevolucionConcept(value) ? false : current.vat_applied,
    }));
    if (suggested !== null && suggested !== undefined && lines.length === 1) {
      setLines((current) => current.map((line, idx) => (
        idx === 0 ? { ...line, amount: suggested } : line
      )));
    }
  };

  const resetForm = () => {
    setForm({ patient_id: '', patient_name: '', nif: '', address: '', concept: '', vat_applied: false, date: new Date().toISOString().slice(0, 10), notes: '' });
    setLines([{ amount: '', payment_method: '', concept: '' }]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.patient_name) return toast.error('Indica el nombre del paciente');
    if (!form.concept) return toast.error('Indica el concepto');
    const validLines = lines
      .map(l => ({ ...l, amount: Number(l.amount || 0) }))
      .filter(l => l.amount > 0);
    if (validLines.length === 0) return toast.error('Añade al menos un importe mayor que 0');
    if (validLines.some(l => !l.payment_method)) return toast.error('Elige el método de pago de cada línea');

    setBusy(true);
    try {
      // Si hay >=2 líneas, enviamos desglosado. Si es solo 1, usamos también
      // los campos legacy `amount` + `payment_method` para mantener compat
      // con vistas/reportes antiguos.
      const payload = {
        ...form,
        concept: canonicalCobroConcept(form.concept, cobroPresets),
        patient_id: form.patient_id || null,
        vat_applied: esDevolucion ? false : form.vat_applied,
        // El importe lleva el IVA dentro: sin esta marca el servidor rechaza
        // el cobro con IVA (pestañas de antes del 24/9/2026).
        iva_dentro: true,
        amount: signedCobroAmount(validLines.reduce((s, l) => s + l.amount, 0), form.concept),
        payment_method: validLines[0].payment_method,
      };
      if (validLines.length > 1) {
        payload.lines = validLines.map((l) => ({ ...l, amount: signedCobroAmount(l.amount, form.concept) }));
      }
      const response = await api.post('/api/cobros', payload);
      if (esDevolucion) toast.success(`Devolución registrada · ${formatEUR(payload.amount)}`);
      else toast.success(validLines.length > 1 ? `Cobro registrado (${validLines.length} líneas)` : 'Cobro registrado');
      const created = response.data;
      // El apunte recién hecho solo se pega a la lista si PERTENECE a lo que
      // se está mirando — y lo que se mira no es lo mismo para cada quien: el
      // mes en dirección, EL DÍA en el mostrador. Comprobarlo siempre contra
      // el mes (como se hacía) metía un cobro de HOY dentro de la caja de
      // AYER que una administrativa estuviera repasando: la cabecera seguía
      // diciendo «Caja del 16/09» y el total ya llevaba dinero del 17. En una
      // caja que se cuadra a mano, eso es un descuadre que nadie ve venir.
      const fechaCreado = String(created?.date || '');
      const entraEnLoQueSeMira = esDireccion
        ? (!month || fechaCreado.startsWith(month))
        : fechaCreado.startsWith(dia);
      if (created && entraEnLoQueSeMira) {
        setItems((current) => [created, ...current.filter((item) => item.id !== created.id)]);
      }
      resetForm();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); } finally { setBusy(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm('¿Eliminar cobro?')) return;
    // 23/9/2026: desde el 22/9 (D6, «lo paras y avisas») el servidor contesta
    // 409 al borrar un cobro ya devuelto, y aquí no había try/catch: no se
    // borraba y el mostrador no se enteraba de nada. Se enseña el motivo tal
    // cual lo dice el servidor; sin él, un aviso genérico, nunca silencio.
    try {
      await api.delete(`/api/cobros/${id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo borrar el cobro');
      return;
    }
    toast.success('Eliminado');
    fetchItems();
  };

  const exportExcel = async () => {
    const params = new URLSearchParams();
    if (month) params.append('month', month);
    if (q) params.append('q', q);
    const ok = await downloadAuth(`${BACKEND_URL}/api/cobros/export-excel?${params.toString()}`, `cobros_${month || 'todos'}.xlsx`);
    // El «descargado» solo se canta si el fichero ha llegado de verdad.
    if (ok) toast.success('Excel descargado');
  };

  // La cabecera dice EXACTAMENTE lo que se está mirando, y eso aquí importa
  // dos veces: a una administrativa un «Total 2026-09» le estaría enseñando
  // justo lo que no debe ver —el mes—, y un «0,00 €» cuando el listado ha
  // fallado parece una caja vacía, que en una pantalla de dinero es el peor
  // error: el que no se ve.
  const subtitulo = loading
    ? 'Cargando…'
    : fallo
      ? 'No se han podido cargar los cobros'
      : esDireccion
        ? `Total ${month}: ${formatEUR(total)} · ${items.length} cobros`
        : `Caja del ${formatDate(dia)}: ${formatEUR(total)} · ${items.length} cobros`;

  return (
    <div>
      <PageHeader title="Cobros" subtitle={subtitulo}
        actions={(esDireccion || canExportExcel) ? (
          <>
            {esDireccion && (
              <Button variant="outline" onClick={alternarVistaIva} data-testid="cobros-revisar-iva">
                {vistaIva ? <><ArrowLeft className="w-4 h-4 mr-2" /> Volver a cobros</> : (
                  <>
                    Revisar IVA antiguo
                    {(ivaPendientes === '?' || ivaPendientes > 0) && (
                      <span className="ml-2 rounded-full bg-amber-100 text-amber-900 border border-amber-300 px-2 text-xs tabular-nums"
                        title={ivaPendientes === '?' ? 'No se ha podido contar: ábrelo para ver por qué' : 'Cobros por revisar'}
                        data-testid="cobros-revisar-iva-pendientes">
                        {ivaPendientes}
                      </span>
                    )}
                  </>
                )}
              </Button>
            )}
            {canExportExcel && !vistaIva && (
              <Button onClick={exportExcel} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="cobros-export-excel-button"><FileSpreadsheet className="w-4 h-4 mr-2" /> Exportar Excel</Button>
            )}
          </>
        ) : null} />

      {vistaIva ? <RevisionIvaPanel onResumen={(r) => setIvaPendientes(r?.pendientes ?? null)} /> : (<>

      <form onSubmit={submit} className="cp-card p-4 mb-5 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="cobros-quick-create-form">
        <div className="lg:col-span-2"><Label>Paciente</Label><PatientCombobox value={form.patient_id} onChange={onPatientPick} patients={patients} placeholder="— buscar paciente por nombre o NIF —" dataTestid="cobro-patient-select" />
          <Input value={form.patient_name} onChange={(e) => setForm(f => ({ ...f, patient_name: e.target.value, patient_id: '' }))} placeholder="Nombre paciente" className="mt-1" data-testid="cobro-patient-name" />
        </div>
        <div><Label>NIF</Label><Input value={form.nif} onChange={(e) => setForm(f => ({ ...f, nif: e.target.value }))} data-testid="cobro-nif" /></div>
        <div className="lg:col-span-2"><Label>Dirección</Label><Input value={form.address} onChange={(e) => setForm(f => ({ ...f, address: e.target.value }))} data-testid="cobro-address" /></div>
        <div><Label>Fecha</Label><Input type="date" value={form.date} onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))} /></div>
        <div className="lg:col-span-3">
          <Label>Concepto</Label>
          <ConceptoCobroField
            value={form.concept}
            onChange={updateConcept}
            dataTestid="cobro-concept"
          />
        </div>
        <div className="lg:col-span-2 flex items-center gap-2 pt-7">
          <Checkbox checked={form.vat_applied && !esDevolucion} disabled={esDevolucion} onCheckedChange={(v) => setForm(f => ({ ...f, vat_applied: !!v }))} data-testid="cobro-vat" />
          <span className="text-sm">{esDevolucion ? 'Sin IVA: es una devolución' : 'Con IVA 21% (incluido en el importe)'}</span>
        </div>
        <div className="lg:col-span-6 border-t border-border pt-3 mt-1">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-[#13231E] font-semibold">{esDevolucion ? 'Importe devuelto' : 'Pagos del cobro'}</Label>
            <span className="text-xs text-muted-foreground">Total líneas: <strong className={`tabular-nums ${esDevolucion ? 'text-red-700' : 'text-[#13231E]'}`} data-testid="cobro-lines-total">{formatEUR(esDevolucion ? -linesTotal : linesTotal)}</strong></span>
          </div>
          {form.vat_applied && !esDevolucion && (
            <div className="text-xs text-muted-foreground mb-2 text-right tabular-nums" data-testid="cobro-desglose-iva">
              Base imponible {formatEUR(desgloseLineas.base)} · IVA (21%) {formatEUR(desgloseLineas.iva)} · Total {formatEUR(desgloseLineas.total)}
            </div>
          )}
          <div className="space-y-2" data-testid="cobro-lines-editor">
            {lines.map((ln, idx) => (
              <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                <div className="md:col-span-3">
                  {idx === 0 && <Label className="text-xs">{esDevolucion ? 'Importe devuelto €' : 'Importe que paga €'}</Label>}
                  <Input
                    type="number"
                    list={`cobro-amount-options-${idx}`}
                    step="0.01"
                    min="0"
                    value={ln.amount}
                    onChange={(e) => updateLine(idx, 'amount', e.target.value)}
                    placeholder="0.00"
                    data-testid={`cobro-line-amount-${idx}`}
                  />
                  <datalist id={`cobro-amount-options-${idx}`}>
                    {amountOptions.map((amount) => <option key={amount} value={amount} label={`${amount} €`} />)}
                  </datalist>
                </div>
                <div className="md:col-span-3">
                  {idx === 0 && <Label className="text-xs">Método</Label>}
                  <Select value={ln.payment_method} onValueChange={(v) => updateLine(idx, 'payment_method', v)}>
                    <SelectTrigger data-testid={`cobro-line-method-${idx}`}><SelectValue placeholder="— Elige —" /></SelectTrigger>
                    <SelectContent>{PAYMENT_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-5">
                  {idx === 0 && <Label className="text-xs">Concepto de la línea (opcional)</Label>}
                  <Input
                    value={ln.concept}
                    onChange={(e) => updateLine(idx, 'concept', e.target.value)}
                    placeholder="Ej: Reserva / Resto / Anticipo…"
                    data-testid={`cobro-line-concept-${idx}`}
                  />
                </div>
                <div className="md:col-span-1 flex justify-end">
                  {lines.length > 1 ? (
                    <Button type="button" size="sm" variant="ghost" onClick={() => removeLine(idx)} className="text-destructive" data-testid={`cobro-line-remove-${idx}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-2 flex flex-wrap gap-2 items-center">
            <Button type="button" size="sm" variant="outline" onClick={addLine} data-testid="cobro-add-line">
              <Plus className="w-3.5 h-3.5 mr-1" /> Añadir otro método de pago
            </Button>
            <span className="text-xs text-muted-foreground">
              Puedes desglosar un mismo cobro en varios métodos (ej.: 1.000€ tarjeta + 500€ Bizum).
            </span>
          </div>
        </div>
        <div className="lg:col-span-6 flex items-end justify-end"><Button type="submit" disabled={busy} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="cobro-submit-button"><Plus className="w-4 h-4 mr-2" /> {esDevolucion ? 'Registrar devolución' : 'Registrar cobro'}</Button></div>
      </form>

      <div className="cp-card p-3 mb-4 flex flex-wrap gap-2 items-end" data-testid="cobros-filtros">
        {esDireccion ? (
          <>
            <div><Label>Mes</Label><Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="cobros-month-filter" /></div>
            <div className="flex-1 min-w-[200px]"><Label>Búsqueda</Label><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="paciente, concepto, NIF…" data-testid="cobros-search" /></div>
          </>
        ) : (
          <>
            <div>
              <Label>Día</Label>
              {/* Un `date` vacío se ignora a propósito: si se borra la fecha no
                  hay caja que enseñar, y es mejor quedarse en el último día
                  elegido que pedir «todo» al servidor (que además lo negaría). */}
              <Input
                type="date"
                value={dia}
                onChange={(e) => { const v = e.target.value; if (v) setDia(v); }}
                className="w-44"
                data-testid="cobros-day-filter"
              />
            </div>
            <div className="text-xs text-muted-foreground pb-2" data-testid="cobros-solo-dia">
              La caja de un día, para cuadrarla. El mes, el histórico y los indicadores son de dirección.
            </div>
          </>
        )}
      </div>

      <div className="cp-card overflow-hidden">
        {loading ? <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>
          : fallo ? (
            <div className="py-8 px-4 text-center" data-testid="cobros-fallo">
              <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-amber-600" />
              <div className="text-sm font-medium text-[#13231E]">No se han podido cargar los cobros</div>
              <div className="text-sm text-muted-foreground mt-1">{fallo}</div>
            </div>
          )
          : items.length === 0 ? <EmptyState icon={Receipt} title="Sin cobros" description="Registra el primero arriba." />
          : <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="bg-[rgba(46,110,94,0.04)] text-left"><tr><th className="py-3 px-4 font-semibold text-[#13231E]">Fecha</th><th className="py-3 px-4 font-semibold text-[#13231E]">Paciente</th><th className="py-3 px-4 font-semibold text-[#13231E]">NIF</th><th className="py-3 px-4 font-semibold text-[#13231E]">Dirección</th><th className="py-3 px-4 font-semibold text-[#13231E]">Concepto</th><th className="py-3 px-4 font-semibold text-[#13231E]">Método</th><th className="py-3 px-4 font-semibold text-[#13231E]">Registrado por</th><th className="py-3 px-4 font-semibold text-[#13231E] text-right">Base</th><th className="py-3 px-4 font-semibold text-[#13231E] text-right">IVA 21%</th><th className="py-3 px-4 font-semibold text-[#13231E] text-right">Total</th><th></th></tr></thead>
            <tbody>{items.map(c => <tr key={c.id} className="border-t border-border hover:bg-[rgba(46,110,94,0.03)]" data-testid="cobro-row">
              <td className="py-3 px-4 text-xs">{formatDate(c.date)}</td>
              <td className="py-3 px-4 font-medium">{c.patient_name}</td>
              <td className="py-3 px-4">{c.nif || '—'}</td>
              <td className="py-3 px-4 text-xs truncate max-w-[220px]">{c.address || '—'}</td>
              <td className="py-3 px-4">
                {c.concept}
                {(c.source === 'devolucion' || Number(c.total ?? c.amount ?? 0) < 0) && (
                  <span className="ml-1.5 text-[10px] bg-red-50 text-red-800 border border-red-200 px-1.5 py-0.5 rounded align-middle" data-testid={`cobro-devolucion-${c.id}`}>devolución</span>
                )}
              </td>
              <td className="py-3 px-4 text-xs">
                {Array.isArray(c.lines) && c.lines.length > 1 ? (
                  <div className="space-y-0.5">
                    {c.lines.map((ln, i) => (
                      <div key={i} className="flex items-center gap-1 text-[11px]">
                        <span className="inline-block min-w-[68px] tabular-nums">{formatEUR(ln.amount)}</span>
                        <span className="text-muted-foreground">·</span>
                        <span>{ln.payment_method}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  c.payment_method
                )}
              </td>
              <td className="py-3 px-4 text-xs">{c.created_by_name || '—'}</td>
              <td className="py-3 px-4 text-right tabular-nums">{formatEUR(c.amount_base ?? c.amount)}</td>
              {/* El IVA en euros, de lo guardado: los cobros de antes del
                  24/9/2026 lo llevan encima y los de ahora dentro, y ninguno
                  se recalcula. */}
              <td className="py-3 px-4 text-right text-xs tabular-nums" data-testid={`cobro-iva-${c.id}`}
                title={c.vat_applied ? (esIvaIncluido(c) ? 'IVA incluido en el importe' : 'IVA sumado al importe (cobro anterior al 24/9/2026)') : undefined}>
                {c.vat_applied ? formatEUR(desgloseGuardado(c).iva) : '—'}
              </td>
              <td className={`py-3 px-4 text-right font-semibold tabular-nums ${Number(c.total ?? c.amount ?? 0) < 0 ? 'text-red-700' : 'text-[#13231E]'}`}>{formatEUR(c.total ?? c.amount)}</td>
              <td className="py-3 px-4 text-right">
                {canEditDelete && (
                  <Button size="sm" variant="ghost" onClick={() => onDelete(c.id)} className="text-destructive">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </td>
            </tr>)}</tbody>
          </table></div>}
      </div>
      </>)}
    </div>
  );
}
